import { useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";
import { Select, Space } from "antd";
import { listGoodsCategories } from "@/api/shopGoods";
import type { EntityId, GoodsCategory, GoodsCategoryOption } from "@/types/shopGoods";

interface ProductCategorySelectProps {
  value?: EntityId[];
  onChange?: (value: EntityId[]) => void;
  existingCategories?: GoodsCategory[];
  disabled?: boolean;
  id?: string;
}

export function ProductCategorySelect({
  value,
  onChange,
  existingCategories,
  disabled,
  id,
}: ProductCategorySelectProps): ReactElement {
  const [categories, setCategories] = useState<GoodsCategoryOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setFailed(false);
    void listGoodsCategories(controller.signal)
      .then(({ data }) => {
        if (!controller.signal.aborted) setCategories(data ?? []);
      })
      .catch(() => {
        if (!controller.signal.aborted) setFailed(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return (): void => controller.abort();
  }, []);

  const options = useMemo(() => {
    const result = new Map(categories.map((category) => [String(category.id), {
      value: String(category.id),
      label: category.categoryName,
      disabled: category.status !== 1,
    }]));
    for (const category of existingCategories ?? []) {
      const key = String(category.categoryId);
      if (!result.has(key)) result.set(key, {
        value: key,
        label: category.categoryName || `分类 ${key}`,
        disabled: true,
      });
    }
    return Array.from(result.values());
  }, [categories, existingCategories]);
  const handleChange = (ids: string[]): void => onChange?.(ids);

  return (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Select
        id={id}
        mode="multiple"
        allowClear
        showSearch
        optionFilterProp="label"
        placeholder="请选择商品分类，可多选"
        style={{ width: "100%" }}
        value={value?.map(String) ?? []}
        onChange={handleChange}
        options={options}
        loading={loading}
        disabled={disabled || loading || failed}
      />
    </Space>
  );
}
